import { useRef, useState, useCallback } from 'react';
import { Animated, NativeSyntheticEvent, NativeScrollEvent, Platform } from 'react-native';

const USE_NATIVE_DRIVER = Platform.OS !== 'web';
const HEADER_SPRING = { tension: 100, friction: 15, useNativeDriver: USE_NATIVE_DRIVER };
const SCROLL_THRESHOLD = 10;
const DEFAULT_HEADER_HEIGHT = 70;

export function useScrollHideHeader({
  isDesktopShell,
  onScrollThreshold,
}: {
  isDesktopShell: boolean;
  onScrollThreshold?: () => void;
}) {
  const headerTranslateY = useRef(new Animated.Value(0)).current;
  const lastScrollY = useRef(0);
  const isHeaderVisible = useRef(true);
  const headerHeightRef = useRef(DEFAULT_HEADER_HEIGHT);
  const [measuredHeaderHeight, setMeasuredHeaderHeight] = useState(DEFAULT_HEADER_HEIGHT);

  const onHeaderLayout = useCallback((event: { nativeEvent: { layout: { height: number } } }) => {
    const h = event.nativeEvent.layout.height;
    headerHeightRef.current = h;
    setMeasuredHeaderHeight(h);
  }, []);

  const handleScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      // Desktop shell: a self-hiding header inside a fixed pane is a mobile
      // idiom — keep the page header pinned.
      if (isDesktopShell) return;
      const currentScrollY = event.nativeEvent.contentOffset.y;
      const diff = currentScrollY - lastScrollY.current;

      if (Math.abs(diff) > SCROLL_THRESHOLD) {
        onScrollThreshold?.();
        if (diff > 0 && currentScrollY > headerHeightRef.current && isHeaderVisible.current) {
          // Scrolling down past header — hide
          isHeaderVisible.current = false;
          Animated.spring(headerTranslateY, {
            toValue: -headerHeightRef.current,
            ...HEADER_SPRING,
          }).start();
        } else if (diff < 0 && !isHeaderVisible.current) {
          // Scrolling up — show
          isHeaderVisible.current = true;
          Animated.spring(headerTranslateY, {
            toValue: 0,
            ...HEADER_SPRING,
          }).start();
        }
        lastScrollY.current = currentScrollY;
      }

      // Always show when at top
      if (currentScrollY <= 0 && !isHeaderVisible.current) {
        isHeaderVisible.current = true;
        Animated.spring(headerTranslateY, {
          toValue: 0,
          ...HEADER_SPRING,
        }).start();
      }
    },
    [headerTranslateY, isDesktopShell, onScrollThreshold]
  );

  return { headerTranslateY, measuredHeaderHeight, onHeaderLayout, handleScroll };
}
